import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { BenchmarkResultPreview } from "@/components/benchmark";
import { ResultErrorState, ResultLoadingState } from "@/components/result";
import { routes, strings } from "@/constants";
import { BenchmarkLayoutVariantEnum, ResultPageStatusEnum } from "@/enums";
import { useBenchmarkPolling } from "@/hooks";
import { BenchmarkLayout } from "@/layouts";
import type { ResultPageStatusType } from "@/types";
import { downloadBenchmarkXlsxHandler } from "@/utils";

const buildFileNameHandler = (jobTitle?: string, code?: string): string => {
    const slugSource = jobTitle?.trim() || code || "result";

    const slug = slugSource.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "") || "result";

    return `${strings.result.xlsxFileNamePrefix}-${slug}`;
};

export const ResultPage = () => {
    const navigate = useNavigate();

    const [searchParams] = useSearchParams();

    const {
        errorMessage,
        onPollExisting: pollExistingHandler,
        onReset: resetPollingHandler,
        result: polledResult,
        status: pollingStatus,
    } = useBenchmarkPolling();

    const hasFetchedRef = useRef(false);

    const jobId = searchParams.get(strings.queryParams.jobId);

    const code = searchParams.get(strings.queryParams.code) || "";

    const hasValidParams = !!jobId && !!code;

    const status: ResultPageStatusType = pollingStatus === "success"
        ? ResultPageStatusEnum.SUCCESS
        : pollingStatus === "error"
            ? ResultPageStatusEnum.ERROR
            : ResultPageStatusEnum.LOADING;

    const layoutVariant = status === ResultPageStatusEnum.ERROR ? BenchmarkLayoutVariantEnum.ERROR : BenchmarkLayoutVariantEnum.DEFAULT;

    const resetHandler = () => {
        resetPollingHandler();

        navigate(routes.benchmark);
    };

    const downloadHandler = () => {
        if (!polledResult) return;

        downloadBenchmarkXlsxHandler(
            {
                industry: polledResult.industry,
                jobTitle: polledResult.jobTitle,
                result: polledResult.resultPayload,
                seniority: polledResult.seniority,
            },
            buildFileNameHandler(
                polledResult.jobTitle,
                code,
            ),
        );
    };

    useEffect(
        () => {
            if (!hasValidParams) {
                navigate(routes.benchmark);

                return;
            }

            if (hasFetchedRef.current) return;

            hasFetchedRef.current = true;

            pollExistingHandler({ jobId: jobId as string });
        },
        [
            hasValidParams,
            jobId,
            navigate,
            pollExistingHandler,
        ],
    );

    if (!hasValidParams) return null;

    if (status === ResultPageStatusEnum.LOADING) {
        return (
            <BenchmarkLayout>
                <ResultLoadingState
                    code={code}
                    onReset={resetHandler}
                />
            </BenchmarkLayout>
        );
    }

    if (status === ResultPageStatusEnum.ERROR) {
        return (
            <BenchmarkLayout variant={layoutVariant}>
                <ResultErrorState
                    code={code}
                    errorMessage={errorMessage}
                    onReset={resetHandler}
                />
            </BenchmarkLayout>
        );
    }

    if (!polledResult) {
        return (
            <BenchmarkLayout>
                <div className="flex items-center justify-center">
                    <span className="text-text-muted">{strings.result.loading}</span>
                </div>
            </BenchmarkLayout>
        );
    }

    return (
        <BenchmarkLayout isCentered={false}>
            <BenchmarkResultPreview
                industry={polledResult.industry}
                jobTitle={polledResult.jobTitle}
                result={polledResult.resultPayload}
                seniority={polledResult.seniority}
                onDownload={downloadHandler}
                onReset={resetHandler}
            />
        </BenchmarkLayout>
    );
};
